"use client"

import { normalizeTextEncoding } from "@/lib/util/text-encoding"
import { StoreRegion } from "@/types/store"
import { Button } from "@medusajs/ui"
import ProductRail from "@modules/home/components/featured-products/product-rail"
import Link from "next/link"
import { useEffect, useState } from "react"
import CategoryProductsLoading from "./category-products-loading"
import { useTranslation } from "react-i18next"

type CategoryProductsProps = {
  categoryId: number | string
  categoryName: string
  categoryHandle: string
  initialPageSize?: number
  region: StoreRegion
  products?: StoreProduct[] // Optional pre-fetched products
}

type ProductsResponse<T> = {
  data: T[]
  metadata?: any
}

type StoreProduct = any

// Product fetches go through the Next.js proxy so the server can attach
// the customer's Authorization header (httpOnly cookie not readable by JS)
const PRODUCTS_PROXY = "/api/store-proxy"

const CategoryProducts = ({
  categoryId,
  categoryName,
  categoryHandle,
  initialPageSize = 10,
  region,
  products: preFetchedProducts,
}: CategoryProductsProps) => {
  const { t } = useTranslation()
  const [products, setProducts] = useState<StoreProduct[]>([])
  const [loading, setLoading] = useState(!preFetchedProducts)
  const [error, setError] = useState(false)

  useEffect(() => {
    // If products are pre-fetched, use them directly
    if (preFetchedProducts) {
      setProducts(preFetchedProducts)
      setLoading(false)
      return
    }

    // Otherwise, fetch products (backward compatibility)
    const abortController = new AbortController()
    let isMounted = true

    const fetchProducts = async () => {
      try {
        setLoading(true)
        setError(false)

        // Use inCategoryTree to get products from category and all child categories
        const filters = `filters[0][column]=pcp.category_id&filters[0][operation]=inCategoryTree&filters[0][values][0]=${categoryId}`
        const pageSizeParam = `page_size=${initialPageSize}`
        const queryParams = `${pageSizeParam}&${filters}`

        const response = await fetch(`${PRODUCTS_PROXY}/products?${queryParams}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          signal: abortController.signal,
        })

        if (!isMounted) {
          return
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch products for category ${categoryId}: ${response.statusText}`)
        }

        const productList: ProductsResponse<StoreProduct> = await response.json()

        if (!isMounted) {
          return
        }

        if (productList?.data && Array.isArray(productList.data)) {
          setProducts(productList.data)
        } else {
          setProducts([])
        }
      } catch (err: any) {
        // Ignore abort errors - they're expected in React Strict Mode
        if (err.name === "AbortError") {
          return
        }
        if (isMounted) {
          setError(true)
          setProducts([])
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchProducts()

    return () => {
      isMounted = false
      abortController.abort()
    }
  }, [categoryId, initialPageSize, preFetchedProducts])

  if (loading) {
    return <CategoryProductsLoading />
  }

  if (error || !products || products.length === 0 || !region) {
    return null
  }

  return (
    <div className="content-container mt-8">
      <div className="mb-8 flex items-center justify-between rounded-lg border border-orange-200 bg-accent/60 p-4">
        <div className="flex items-center">
          <div className="min-w-max text-2xl text-ui-fg-subtle capitalize">
            {normalizeTextEncoding(categoryName).toUpperCase()}
          </div>
          <div className="mx-8 h-px w-full bg-button-primary" />
        </div>
        <div className="flex items-center gap-4">
          <Link className="hidden md:flex" href={`/categories/${categoryHandle}`}>
            <Button className="w-fit border border-orange-300 bg-white text-lg text-button-primary transition-all duration-200 ease-in-out hover:scale-110 hover:bg-button-primary hover:text-white">
              {t("home.discoverAll")}
            </Button>
          </Link>
        </div>
      </div>
      <ProductRail product={products} region={region} />
    </div>
  )
}

export default CategoryProducts

